import { deepClone } from '../../deep_clone'
import * as flatbuffers from 'flatbuffers'
import { parse } from 'valibot'
import type { EngineStateSnapshot } from '../../../engine_wrapper'
import { tbs } from '../../../engine/bindings/typescript/engine'
import { createPlainEntity } from '../entity/EntityType/Plain'
import createSampleEntity from '../entity/createSampleEntity'
import type { Entity } from '../entity'
import { EntityLayerId } from '../entity/EntityLayerId/EntityLayerId.enum'
import { EntityTypeId } from '../entity/EntityTypeId.enum'
import type { Player } from '../player/Player.type'
import { StateSchema, type State } from '../state/State.type'
import { ActionType as AppActionType, type ActionType } from '../state/flux/action/ActionType'
import type Action from '../state/flux/action/Action.type'
import { StateMapDisk } from '../state/state_map_disk.enum'

export type PreferredLocalStateMapDisk = StateMapDisk.IndexedDB | StateMapDisk.Memory

const BIGINT_SENTINEL = BigInt(-1)
const NUMBER_SENTINEL = -1

export function encodeActionMessage(action: Action): Uint8Array {
  const actionEnvelope = new tbs.ActionEnvelopeT(
    toActionPayload(action.type),
    toGeneratedAction(action)
  )
  const message = new tbs.EngineMessageT(tbs.EnginePayload.ActionEnvelope, actionEnvelope)
  return encodeEngineMessage(message)
}

export function encodeStateMessage(state: State): Uint8Array {
  return encodeGeneratedStateMessage(toGeneratedState(state))
}

export function encodeGeneratedStateMessage(state: tbs.StateT): Uint8Array {
  const message = new tbs.EngineMessageT(
    tbs.EnginePayload.StateEnvelope,
    new tbs.StateEnvelopeT(state)
  )
  return encodeEngineMessage(message)
}

/**
 * Strict decode for app-facing / external state boundaries.
 *
 * Use this when the input may come from persisted rows, user payloads, or
 * other non-engine sources that still need full handwritten validation.
 */
export function decodeStateSnapshot(snapshot: EngineStateSnapshot, preferredLocalDisk?: PreferredLocalStateMapDisk): State {
  const payload = snapshot.messageObject.payload
  if (!(payload instanceof tbs.StateEnvelopeT) || !payload.state) {
    throw new Error(`[engine-wrapper] expected StateEnvelope, received ${snapshot.payloadName}`)
  }
  return parse(StateSchema, fromGeneratedState(payload.state, preferredLocalDisk))
}

/**
 * Unchecked decode for engine-originated snapshots.
 *
 * Use this for WASM output and bootstrap/default engine state, where the
 * engine itself is the source of truth and the app schema would be redundant.
 */
export function decodeStateSnapshotUnchecked(snapshot: EngineStateSnapshot, preferredLocalDisk?: PreferredLocalStateMapDisk): State {
  const payload = snapshot.messageObject.payload
  if (!(payload instanceof tbs.StateEnvelopeT) || !payload.state) {
    throw new Error(`[engine-wrapper] expected StateEnvelope, received ${snapshot.payloadName}`)
  }
  return fromGeneratedState(payload.state, preferredLocalDisk)
}

/**
 * Strict decode for app-facing / external bytes.
 */
export function decodeStateMessageBytes(bytes: Uint8Array, preferredLocalDisk?: PreferredLocalStateMapDisk): State {
  const message = tbs.EngineMessage.getRootAsEngineMessage(new flatbuffers.ByteBuffer(bytes)).unpack()
  const payload = message.payload
  if (!(payload instanceof tbs.StateEnvelopeT) || !payload.state) {
    throw new Error('Expected StateEnvelope payload')
  }
  return parse(StateSchema, fromGeneratedState(payload.state, preferredLocalDisk))
}

/**
 * Unchecked decode for engine-originated bytes.
 */
export function decodeStateMessageBytesUnchecked(bytes: Uint8Array, preferredLocalDisk?: PreferredLocalStateMapDisk): State {
  const message = tbs.EngineMessage.getRootAsEngineMessage(new flatbuffers.ByteBuffer(bytes)).unpack()
  const payload = message.payload
  if (!(payload instanceof tbs.StateEnvelopeT) || !payload.state) {
    throw new Error('Expected StateEnvelope payload')
  }
  return fromGeneratedState(payload.state, preferredLocalDisk)
}

function encodeEngineMessage(message: tbs.EngineMessageT): Uint8Array {
  const builder = new flatbuffers.Builder(1024)
  const offset = message.pack(builder)
  builder.finish(offset)
  return builder.asUint8Array()
}

function toActionPayload(actionType: ActionType): tbs.ActionPayload {
  switch (actionType) {
    case AppActionType.Game.CancelFactoryOrder: return tbs.ActionPayload.CancelFactoryOrderAction
    case AppActionType.Game.EndGame: return tbs.ActionPayload.EndGameAction
    case AppActionType.Game.EndRound: return tbs.ActionPayload.EndRoundAction
    case AppActionType.Game.EndTurn: return tbs.ActionPayload.EndTurnAction
    case AppActionType.Game.MoveUnit: return tbs.ActionPayload.MoveUnitAction
    case AppActionType.Game.PlaceFactoryOrder: return tbs.ActionPayload.PlaceFactoryOrderAction
    case AppActionType.Game.StartRound: return tbs.ActionPayload.StartRoundAction
    case AppActionType.Game.StartTurn: return tbs.ActionPayload.StartTurnAction
    case AppActionType.Lobby.ClaimPlayerSlot: return tbs.ActionPayload.ClaimPlayerSlotAction
    case AppActionType.Lobby.DeleteLobby: return tbs.ActionPayload.DeleteLobbyAction
    case AppActionType.Lobby.KickPlayerSlot: return tbs.ActionPayload.KickPlayerSlotAction
    case AppActionType.Lobby.LeavePlayerSlot: return tbs.ActionPayload.LeavePlayerSlotAction
    case AppActionType.Lobby.PatchConfig: return tbs.ActionPayload.PatchConfigAction
    case AppActionType.Lobby.ReservePlayerSlot: return tbs.ActionPayload.ReservePlayerSlotAction
    case AppActionType.Lobby.RestoreLobby: return tbs.ActionPayload.RestoreLobbyAction
    case AppActionType.Lobby.SetBotPlayerSlot: return tbs.ActionPayload.SetBotPlayerSlotAction
    case AppActionType.Lobby.StartNewGame: return tbs.ActionPayload.StartNewGameAction
    case AppActionType.Map.AddPlayer: return tbs.ActionPayload.AddPlayerAction
    case AppActionType.Map.DeleteMap: return tbs.ActionPayload.DeleteMapAction
    case AppActionType.Map.DeletePlayer: return tbs.ActionPayload.DeletePlayerAction
    case AppActionType.Map.EraseTile: return tbs.ActionPayload.EraseTileAction
    case AppActionType.Map.MapToLobby: return tbs.ActionPayload.MapToLobbyAction
    case AppActionType.Map.PaintTile: return tbs.ActionPayload.PaintTileAction
    case AppActionType.Map.PatchMap: return tbs.ActionPayload.PatchMapAction
    case AppActionType.Map.PatchPlayer: return tbs.ActionPayload.PatchPlayerAction
    case AppActionType.Map.PublishMap: return tbs.ActionPayload.PublishMapAction
    case AppActionType.Map.RestoreMap: return tbs.ActionPayload.RestoreMapAction
    case AppActionType.Map.RotateEntity: return tbs.ActionPayload.RotateEntityAction
    case AppActionType.Map.RownipulatorAddRow: return tbs.ActionPayload.RownipulatorAddRowAction
    case AppActionType.Map.RownipulatorDeleteRow: return tbs.ActionPayload.RownipulatorDeleteRowAction
    case AppActionType.Map.RownipulatorDuplicateRow: return tbs.ActionPayload.RownipulatorDuplicateRowAction
    case AppActionType.Map.SetEntityElevation: return tbs.ActionPayload.SetEntityElevationAction
    case AppActionType.Map.SetEnts: return tbs.ActionPayload.SetEntsAction
    case AppActionType.Map.SetState: return tbs.ActionPayload.SetStateAction
    case AppActionType.Map.UnpublishMap: return tbs.ActionPayload.UnpublishMapAction
    default: throw new Error(`Unsupported WASM action payload: ${actionType}`)
  }
}

function toGeneratedAction(action: Action): tbs.ActionEnvelopeT['action'] {
  switch (action.type) {
    case AppActionType.Game.CancelFactoryOrder:
      return new tbs.CancelFactoryOrderActionT(toWasmActionType(action.type), action.factory_id)
    case AppActionType.Game.EndGame:
      return new tbs.EndGameActionT(toWasmActionType(action.type))
    case AppActionType.Game.EndRound:
      return new tbs.EndRoundActionT(toWasmActionType(action.type))
    case AppActionType.Game.EndTurn:
      return new tbs.EndTurnActionT(toWasmActionType(action.type), action.player_id)
    case AppActionType.Game.MoveUnit:
      return new tbs.MoveUnitActionT(
        toWasmActionType(action.type),
        action.unit_id,
        action.target_id ?? NUMBER_SENTINEL,
        action.pickup_id ?? NUMBER_SENTINEL,
        toPosition(action.start_position),
        toPosition(action.dest_position),
        action.dest_heading ?? NUMBER_SENTINEL,
        toPosition(action.attack_position),
        Boolean(action.annex),
        Boolean(action.wait),
        action.taxi_id ?? NUMBER_SENTINEL,
        action.cargo_id ?? NUMBER_SENTINEL,
        toPosition(action.unload_position),
        action.merge_id ?? NUMBER_SENTINEL
      )
    case AppActionType.Game.PlaceFactoryOrder:
      return new tbs.PlaceFactoryOrderActionT(toWasmActionType(action.type), action.factory_id, action.etype_id)
    case AppActionType.Game.StartRound:
      return new tbs.StartRoundActionT(toWasmActionType(action.type))
    case AppActionType.Game.StartTurn:
      return new tbs.StartTurnActionT(toWasmActionType(action.type), action.player_id)
    case AppActionType.Lobby.ClaimPlayerSlot:
      return new tbs.ClaimPlayerSlotActionT(
        toWasmActionType(action.type),
        action.profile ? toProfileRef({
          id: action.profile.id,
          dname: action.profile.dname ?? null,
          handle: null,
        }) : null,
        action.player_id
      )
    case AppActionType.Lobby.DeleteLobby:
      return new tbs.DeleteLobbyActionT(toWasmActionType(action.type))
    case AppActionType.Lobby.KickPlayerSlot:
      return new tbs.KickPlayerSlotActionT(toWasmActionType(action.type), action.player_id)
    case AppActionType.Lobby.LeavePlayerSlot:
      return new tbs.LeavePlayerSlotActionT(toWasmActionType(action.type), action.player_id)
    case AppActionType.Lobby.PatchConfig:
      return new tbs.PatchConfigActionT(toWasmActionType(action.type), toPatchConfigData(action.data))
    case AppActionType.Lobby.ReservePlayerSlot:
      return new tbs.ReservePlayerSlotActionT(toWasmActionType(action.type), action.player_id)
    case AppActionType.Lobby.RestoreLobby:
      return new tbs.RestoreLobbyActionT(toWasmActionType(action.type))
    case AppActionType.Lobby.SetBotPlayerSlot:
      return new tbs.SetBotPlayerSlotActionT(toWasmActionType(action.type), action.player_id)
    case AppActionType.Lobby.StartNewGame:
      return new tbs.StartNewGameActionT(toWasmActionType(action.type))
    case AppActionType.Map.AddPlayer:
      return new tbs.AddPlayerActionT(toWasmActionType(action.type), toGeneratedPlayer(action.player))
    case AppActionType.Map.DeleteMap:
      return new tbs.DeleteMapActionT(toWasmActionType(action.type))
    case AppActionType.Map.DeletePlayer:
      return new tbs.DeletePlayerActionT(toWasmActionType(action.type), action.playerId)
    case AppActionType.Map.EraseTile:
      return new tbs.EraseTileActionT(
        toWasmActionType(action.type),
        new tbs.EraseTileOptionT(action.options.entity_layer_ids),
        toPosition(action.position)
      )
    case AppActionType.Map.MapToLobby:
      return new tbs.MapToLobbyActionT(toWasmActionType(action.type), toProfileRef(action.profile))
    case AppActionType.Map.PaintTile:
      return new tbs.PaintTileActionT(
        toWasmActionType(action.type),
        new tbs.PaintTileOptionT(
          action.options.dname ?? null,
          action.options.entity ? toGeneratedEntity(action.options.entity) : null,
          Boolean(action.options.assign_selected_player),
          action.options.folder_id ?? 0
        ),
        toPosition(action.position),
        action.selectedPlayerId ?? NUMBER_SENTINEL
      )
    case AppActionType.Map.PatchMap:
      return new tbs.PatchMapActionT(toWasmActionType(action.type), new tbs.PatchMapDataT(action.data.dname ?? null, action.data.bio ?? null))
    case AppActionType.Map.PatchPlayer:
      return new tbs.PatchPlayerActionT(
        toWasmActionType(action.type),
        action.player_id,
        new tbs.PatchPlayerDataT(
          action.data.dname ?? null,
          action.data.dname_placeholder ?? null,
          action.data.money ?? NUMBER_SENTINEL,
          toBigIntOrSentinel(action.data.color1),
          toBigIntOrSentinel(action.data.color2)
        )
      )
    case AppActionType.Map.PublishMap:
      return new tbs.PublishMapActionT(toWasmActionType(action.type))
    case AppActionType.Map.RestoreMap:
      return new tbs.RestoreMapActionT(toWasmActionType(action.type))
    case AppActionType.Map.RotateEntity:
      return new tbs.RotateEntityActionT(toWasmActionType(action.type), action.entityId, action.heading)
    case AppActionType.Map.RownipulatorAddRow:
      return new tbs.RownipulatorAddRowActionT(toWasmActionType(action.type), action.etypeIds, action.heading)
    case AppActionType.Map.RownipulatorDeleteRow:
      return new tbs.RownipulatorDeleteRowActionT(toWasmActionType(action.type), toPosition(action.position), action.orientation)
    case AppActionType.Map.RownipulatorDuplicateRow:
      return new tbs.RownipulatorDuplicateRowActionT(toWasmActionType(action.type), toPosition(action.position), action.orientation)
    case AppActionType.Map.SetEntityElevation:
      return new tbs.SetEntityElevationActionT(
        toWasmActionType(action.type),
        action.data.map((item) => new tbs.EntityElevationUpdateT(
          item.ent_id,
          item.floor_e ?? NUMBER_SENTINEL,
          item.water_e ?? NUMBER_SENTINEL
        ))
      )
    case AppActionType.Map.SetEnts:
      return new tbs.SetEntsActionT(toWasmActionType(action.type), action.ents.map(toGeneratedEntity))
    case AppActionType.Map.SetState:
      return new tbs.SetStateActionT(toWasmActionType(action.type), toGeneratedState(action.state))
    case AppActionType.Map.UnpublishMap:
      return new tbs.UnpublishMapActionT(toWasmActionType(action.type))
    default:
      throw new Error('Unsupported WASM action type')
  }
}

export function toGeneratedState(stateRaw: State): tbs.StateT {
  const state = deepClone(stateRaw) as State
  const engineEntities = toEngineStateEntities(state)
  return new tbs.StateT(
    state.round,
    state.online ? new tbs.StateOnlineT(
      toBigIntOrSentinel(state.online.id),
      state.online.dname ?? null,
      state.online.bio ?? null,
      state.online.passphrase ?? null,
      state.online.initialTime,
      state.online.extraTimePerTurn,
      state.online.maxTimePerTurn
    ) : null,
    new tbs.StateConfigT(
      Boolean(state.config.fow),
      state.config.incomeMult,
      state.config.startIncome,
      state.config.winMapControlPercent ?? NUMBER_SENTINEL,
      Boolean(state.config.winAnnexAnyHq),
      Boolean(state.config.requireCompleteKills),
      state.config.winMaxDays ?? NUMBER_SENTINEL,
      state.config.maxUnitsPerPlayer ?? NUMBER_SENTINEL,
      state.config.restrictedUnits.map((item) => new tbs.RestrictedUnitRuleT(
        item.etype_id,
        item.player_id ?? NUMBER_SENTINEL,
        item.limit ?? NUMBER_SENTINEL,
        item.turn ?? NUMBER_SENTINEL
      ))
    ),
    new tbs.StateMapT(
      toBigIntOrSentinel(state.map.id),
      state.map.dname ?? null,
      state.map.bio ?? null,
      toGeneratedStateMapDisk(state.map.disk),
      false
    ),
    state.type,
    state.width,
    state.height,
    engineEntities.map(toGeneratedEntity),
    state.players.map(toGeneratedPlayer),
    state.turnPlayerId ?? NUMBER_SENTINEL,
    new tbs.RngConfigT(state.rng.str, state.rng.crit, state.rng.seed, state.rng.state),
    Boolean(state.ended)
  )
}

export function toGeneratedPlayer(player: Player): tbs.PlayerT {
  return new tbs.PlayerT(
    player.id,
    toBigIntOrSentinel(player.profile_id),
    player.invite_id ?? null,
    player.bot_id ?? null,
    Boolean(player.is_local),
    player.kind,
    player.dname ?? null,
    player.dname_placeholder ?? null,
    player.color1,
    player.color2,
    player.money,
    player.turn_status,
    player.game_status
  )
}

export function toGeneratedEntity(entity: Entity): tbs.EntityT {
  return new tbs.EntityT(
    entity.etype_id,
    entity.layer_id,
    entity.id,
    entity.player_id ?? NUMBER_SENTINEL,
    entity.x,
    entity.y,
    entity.heading ?? 0,
    entity.hp ?? NUMBER_SENTINEL,
    entity.fuel ?? NUMBER_SENTINEL,
    entity.vision ?? NUMBER_SENTINEL,
    entity.armor ?? NUMBER_SENTINEL,
    entity.mobility ?? NUMBER_SENTINEL,
    entity.mtype_id ?? tbs.MoveTypeId.None,
    Boolean((entity as Record<string, unknown>).builtThisTurn),
    Boolean((entity as Record<string, unknown>).attackedThisTurn),
    Boolean((entity as Record<string, unknown>).waitedThisTurn),
    Boolean((entity as Record<string, unknown>).destroyed),
    Boolean((entity as Record<string, unknown>).annexed),
    Boolean((entity as Record<string, unknown>).hasError),
    (entity as { cargo?: Array<number> }).cargo ?? [],
    (entity as { taxi_id?: number }).taxi_id ?? NUMBER_SENTINEL,
    ((entity as { weapons?: Array<Record<string, unknown>> }).weapons ?? []).map((weapon) => new tbs.WeaponT(
      (weapon.wt_id as number) ?? tbs.WeaponTypeId.None,
      (weapon.ammo as number | undefined) ?? NUMBER_SENTINEL,
      (weapon.ammops as number | undefined) ?? NUMBER_SENTINEL,
      (weapon.maxAmmo as number | undefined) ?? NUMBER_SENTINEL,
      (weapon.str as number | undefined) ?? NUMBER_SENTINEL,
      (weapon.minRange as number | undefined) ?? NUMBER_SENTINEL,
      (weapon.maxRange as number | undefined) ?? NUMBER_SENTINEL,
      Boolean(weapon.ca),
      Boolean(weapon.airOnly)
    )),
    (entity as { floor_e?: number }).floor_e ?? NUMBER_SENTINEL,
    (entity as { water_e?: number }).water_e ?? NUMBER_SENTINEL,
    (entity as { road?: tbs.RoadKind }).road ?? tbs.RoadKind.None,
    (entity as { ap?: number }).ap ?? NUMBER_SENTINEL,
    (entity as { ap_ent_id?: number }).ap_ent_id ?? NUMBER_SENTINEL
  )
}

function fromGeneratedState(state: tbs.StateT, preferredLocalDisk?: PreferredLocalStateMapDisk): State {
  const decodedEntities = state.ents.map(fromGeneratedEntity)
  const payload: Record<string, unknown> = {
    round: state.round,
    config: {
      fow: state.config?.fow ? 1 : 0,
      incomeMult: state.config?.incomeMult ?? 1,
      startIncome: state.config?.startIncome ?? 0,
      winMapControlPercent: fromSentinelNumber(state.config?.winMapControlPercent),
      winAnnexAnyHq: state.config?.winAnnexAnyHq ? 1 : 0,
      requireCompleteKills: state.config?.requireCompleteKills ? 1 : 0,
      winMaxDays: fromSentinelNumber(state.config?.winMaxDays),
      maxUnitsPerPlayer: fromSentinelNumber(state.config?.maxUnitsPerPlayer),
      restrictedUnits: (state.config?.restrictedUnits ?? []).map((item) => {
        const rule: Record<string, unknown> = {
          etype_id: item.etypeId,
        }
        if (item.playerId >= 0) rule.player_id = item.playerId
        if (item.limit >= 0) rule.limit = item.limit
        if (item.turn >= 0) rule.turn = item.turn
        return rule
      }),
    },
    map: {
      id: fromSentinelBigInt(state.map?.id),
      dname: byteStringToString(state.map?.dname),
      bio: byteStringToString(state.map?.bio),
      disk: fromGeneratedStateMapDisk(state.map?.disk, preferredLocalDisk),
    },
    type: state.type,
    width: state.width,
    height: state.height,
    ents: withImplicitPlainTerrain(state.width, state.height, decodedEntities),
    players: state.players.map(fromGeneratedPlayer),
    turnPlayerId: state.turnPlayerId >= 0 ? state.turnPlayerId : null,
    rng: {
      str: state.rng?.str ?? 10,
      crit: state.rng?.crit ?? 10,
      seed: state.rng?.seed ?? 0,
      state: state.rng?.state ?? 0,
    },
  }

  if (state.online) {
    payload.online = {
      id: fromSentinelBigInt(state.online.id),
      dname: byteStringToString(state.online.dname),
      bio: byteStringToString(state.online.bio),
      passphrase: byteStringToString(state.online.passphrase),
      initialTime: state.online.initialTime,
      extraTimePerTurn: state.online.extraTimePerTurn,
      maxTimePerTurn: state.online.maxTimePerTurn,
    }
  } else {
    payload.online = null
  }

  if (state.ended) {
    payload.ended = true
  }

  return payload as State
}

function fromGeneratedPlayer(player: tbs.PlayerT): Player {
  const payload: Record<string, unknown> = {
    id: player.id,
    profile_id: fromSentinelBigInt(player.profileId),
    kind: player.kind,
    dname: byteStringToString(player.dname),
    dname_placeholder: byteStringToString(player.dnamePlaceholder),
    color1: player.color1,
    color2: player.color2,
    money: player.money,
    turn_status: player.turnStatus,
    game_status: player.gameStatus,
  }
  if (player.inviteId !== null) payload.invite_id = byteStringToString(player.inviteId)
  if (player.botId !== null) payload.bot_id = byteStringToString(player.botId)
  if (player.isLocal) payload.is_local = true
  return payload as Player
}

function fromGeneratedEntity(entity: tbs.EntityT): Entity {
  const payload: Record<string, unknown> = {
    ...createSampleEntity(entity.etypeId, entity.playerId >= 0 ? entity.playerId : null, entity.id),
    x: entity.x,
    y: entity.y,
    heading: entity.heading,
  }
  if (entity.playerId >= 0) payload.player_id = entity.playerId
  if (entity.builtThisTurn) payload.builtThisTurn = true
  if (entity.attackedThisTurn) payload.attackedThisTurn = true
  if (entity.waitedThisTurn) payload.waitedThisTurn = true
  if (entity.destroyed) payload.destroyed = true
  if (entity.annexed) payload.annexed = true
  if (entity.hasError) payload.hasError = true
  if (entity.hp >= 0) payload.hp = entity.hp
  if (entity.fuel >= 0) payload.fuel = entity.fuel
  if (entity.vision >= 0) payload.vision = entity.vision
  if (entity.armor >= 0) payload.armor = entity.armor
  if (entity.mobility >= 0) payload.mobility = entity.mobility
  if (entity.mtypeId !== tbs.MoveTypeId.None) payload.mtype_id = entity.mtypeId
  if (entity.cargo.length > 0) payload.cargo = entity.cargo
  if (entity.taxiId >= 0) payload.taxi_id = entity.taxiId
  if (entity.weapons.length > 0) {
    payload.weapons = entity.weapons.map((weapon) => {
      const item: Record<string, unknown> = {
        wt_id: weapon.wtId,
      }
      if (weapon.ammo >= 0) item.ammo = weapon.ammo
      if (weapon.ammops >= 0) item.ammops = weapon.ammops
      if (weapon.maxAmmo >= 0) item.maxAmmo = weapon.maxAmmo
      if (weapon.str >= 0) item.str = weapon.str
      if (weapon.minRange >= 0) item.minRange = weapon.minRange
      if (weapon.maxRange >= 0) item.maxRange = weapon.maxRange
      if (weapon.ca) item.ca = true
      if (weapon.airOnly) item.airOnly = true
      return item
    })
  }
  if (entity.floorE >= 0) payload.floor_e = entity.floorE
  if (entity.waterE >= 0) payload.water_e = entity.waterE
  if (entity.road !== tbs.RoadKind.None) payload.road = entity.road
  if (entity.ap >= 0) payload.ap = entity.ap
  if (entity.apEntId >= 0) payload.ap_ent_id = entity.apEntId
  return payload as Entity
}

function toEngineStateEntities(stateRaw: State): Array<Entity> {
  const state = deepClone(stateRaw) as State
  if (!state.ents.some(isImplicitPlainTerrainCandidateAnchor)) {
    return state.ents
  }
  return state.ents.filter((entity) => !isImplicitPlainTerrain(entity))
}

function withImplicitPlainTerrain(width: number, height: number, entities: Array<Entity>): Array<Entity> {
  if (!entities.some(isImplicitPlainTerrainCandidateAnchor)) {
    return entities
  }

  const terrainLayer1ByCoord = new Map<string, Entity>()
  const remainingEntities: Array<Entity> = []
  const maxEntityId = entities.reduce((max, entity) => Math.max(max, entity.id), 0)

  for (const entity of entities) {
    if (entity.layer_id === EntityLayerId.TerrainL1) {
      terrainLayer1ByCoord.set(coordKey(entity.x, entity.y), entity)
    } else {
      remainingEntities.push(entity)
    }
  }

  const normalizedEntities: Array<Entity> = []
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      normalizedEntities.push(
        terrainLayer1ByCoord.get(coordKey(x, y))
        ?? createImplicitPlainEntity(maxEntityId, width, x, y)
      )
    }
  }

  return [...normalizedEntities, ...remainingEntities]
}

function isImplicitPlainTerrain(entity: Entity): boolean {
  return entity.etype_id === EntityTypeId.Plain && entity.layer_id === EntityLayerId.TerrainL1
}

function isImplicitPlainTerrainCandidateAnchor(entity: Entity): boolean {
  return !isImplicitPlainTerrain(entity)
}

function createImplicitPlainEntity(maxEntityId: number, width: number, x: number, y: number) {
  return createPlainEntity(
    maxEntityId + (y * width) + x + 1,
    x,
    y
  )
}

function coordKey(x: number, y: number): string {
  return `${x},${y}`
}

function toPatchConfigData(data: Record<string, unknown>): tbs.PatchConfigDataT {
  return new tbs.PatchConfigDataT(
    Boolean(data.fow),
    (data.incomeMult as number | undefined) ?? NUMBER_SENTINEL,
    (data.startIncome as number | undefined) ?? NUMBER_SENTINEL,
    (data.winMapControlPercent as number | undefined) ?? NUMBER_SENTINEL,
    Boolean(data.winAnnexAnyHq),
    Boolean(data.requireCompleteKills),
    (data.winMaxDays as number | undefined) ?? NUMBER_SENTINEL,
    (data.maxUnitsPerPlayer as number | undefined) ?? NUMBER_SENTINEL
  )
}

function toProfileRef(profile: { id: number | null, dname: string | null, handle?: string | null }): tbs.ProfileRefT {
  return new tbs.ProfileRefT(
    toBigIntOrSentinel(profile.id),
    profile.dname ?? null,
    profile.handle ?? null
  )
}

function toPosition(position: { x: number, y: number } | null | undefined): tbs.PositionT | null {
  if (!position) {
    return null
  }
  return new tbs.PositionT(position.x, position.y)
}

function toWasmActionType(actionType: ActionType): tbs.ActionType {
  return actionType as unknown as tbs.ActionType
}

function toBigIntOrSentinel(value: number | null | undefined): bigint {
  return value == null ? BIGINT_SENTINEL : BigInt(value)
}

function fromSentinelBigInt(value: bigint | null | undefined): number | null {
  if (value == null || value === BIGINT_SENTINEL) {
    return null
  }
  return Number(value)
}

function fromSentinelNumber(value: number | null | undefined): number | null {
  if (value == null || value === NUMBER_SENTINEL) {
    return null
  }
  return value
}

function byteStringToString(value: string | Uint8Array | null | undefined): string | null {
  if (value == null) {
    return null
  }
  if (typeof value === 'string') {
    return value
  }
  return new TextDecoder().decode(value)
}

function toGeneratedStateMapDisk(value: StateMapDisk): tbs.StateMapDisk {
  switch (value) {
    case StateMapDisk.Cloud:
      return tbs.StateMapDisk.Cloud
    case StateMapDisk.IndexedDB:
    case StateMapDisk.Memory:
      return tbs.StateMapDisk.LocalStorage
    case StateMapDisk.Export:
      throw new Error('Export disk placeholder is not supported by the engine codec')
    case StateMapDisk.Workspace:
      throw new Error('Workspace disk placeholder is not supported by the engine codec')
  }
}

function fromGeneratedStateMapDisk(
  value: tbs.StateMapDisk | null | undefined,
  preferredLocalDisk?: PreferredLocalStateMapDisk
): StateMapDisk {
  switch (value) {
    case tbs.StateMapDisk.LocalStorage:
      return preferredLocalDisk || StateMapDisk.IndexedDB
    case tbs.StateMapDisk.Cloud:
    default:
      return StateMapDisk.Cloud
  }
}
